import { FC, useMemo, useState } from 'react';
import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CloseOutlined } from '@ant-design/icons';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { useForm } from 'react-hook-form';
import { readingInventorySubRoutes } from '@router/Readings.routes';
import { schemaInventoryItemBase, transformNameToSingle } from './helpers';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { ClinicalTrialInventoryBaseItemTypeState } from './ClinicalTrialInventoryTable';

export type ClinicalTrialInventoryItemName =
  (typeof readingInventorySubRoutes)[number]['key'];

type CreateItemClinicalTrialInventoryProps = {
  type: ClinicalTrialInventoryItemName;
  close: () => void;
  navigateToItem: (item: ClinicalTrialInventoryBaseItemTypeState) => void;
};

export const CreateItemClinicalTrialInventory: FC<
  CreateItemClinicalTrialInventoryProps
> = ({ type, close, navigateToItem }) => {
  const defaultValues = {
    name: '',
    code: '',
    description: '',
  };
  const { watch, setValue, trigger, formState, getValues } = useForm({
    resolver: yupResolver(schemaInventoryItemBase),
    defaultValues,
  });
  const { errors, isValid, isDirty, touchedFields } = formState;

  const [askQuit, setAskQuit] = useState<boolean>(false);

  const transformedType = useMemo(
    () => transformNameToSingle(type as string),
    [type]
  );

  const onChange = async (
    fieldName: keyof typeof defaultValues,
    value: string
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };

  const onBlur = (fieldName: keyof typeof defaultValues, value: string) => {
    if (touchedFields[fieldName]) {
      onChange(fieldName, value.trim());
    }
  };

  const onClose = () => {
    if (isDirty) {
      setAskQuit(true);
    } else {
      close && close();
    }
  };
  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={`Add New ${transformedType}`}
      open
      width={464}
      placement='right'
      onClose={onClose}
      closeIcon={<CloseOutlined />}
      data-testid='add-ct-inventory-item'
    >
      <form>
        <Input
          required
          value={watch('name')}
          name={'name'}
          onChange={e => onChange('name', e.target.value)}
          onBlur={e => onBlur('name', e.target.value)}
          data-testid={'item-name'}
          errors={errors.name?.message}
          label={'Name'}
          placeholder={'Name'}
          classNameInputWrapper='mb-3'
          maxLength={255}
        />
        <Input
          required
          value={watch('code')}
          name={'code'}
          onChange={e => onChange('code', e.target.value)}
          onBlur={e => onBlur('code', e.target.value)}
          data-testid={'item-code'}
          errors={errors.code?.message}
          label={'Code'}
          placeholder={'Code'}
          classNameInputWrapper='mb-3'
          maxLength={255}
        />
        <TextArea
          className='!resize-none'
          label='Description'
          placeholder='Description'
          value={watch('description')}
          rows={3}
          name='description'
          onChange={e => onChange('description', e.target.value)}
          onBlur={e => onBlur('description', e.target.value)}
          maxLength={700}
          classNameInputWrapper='mb-3'
        />
      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button
            data-testid={'cancel-btn'}
            className='w-[17rem]'
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            data-testid={'ok-btn'}
            className='w-[17rem]'
            type='primary'
            disabled={!isValid}
            onClick={() =>
              navigateToItem({ mode: 'create', data: getValues() })
            }
          >
            Proceed
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        onConfirm={() => close && close()}
        onCancel={() => setAskQuit(false)}
        open={askQuit}
      />
    </Drawer>
  );
};
